import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Import helpers
import { createLinkParams } from 'helpers/index';
import { getProductImage } from 'helpers/search';
import { sendSearchDropdownEvent } from 'helpers/tracking';

// Import components
import { Paragraph } from 'components/elements';
import { ProgramInfo, VodSerialInfo } from './info';
import NoImage from 'components/views/vod/images/placeholder.svg';

// Import styles
import { transitionMain } from 'components/styles';
import { Info, Clock } from './info/styles';

// Import hooks
import useProductRatingCheck from 'components/elements/molecules/parental_control/parental_control_utils/useProductRatingCheck';

// Import variables
import { PRODUCT_TYPES } from 'helpers/variables';

const SearchItem = ({ toggle, product, channels }) => {
	// REPLACE RATING WITH PC_RATING AFTER API UPDATE
	const { uuid, images, screenshots, title, type, subtype, context, pc_rating, genres, country, year } = product;

	const since = context?.since;
	const till = context?.till;
	const duration = context?.duration;

	const { imgUrl, isImgUrlValid, isChannelLogo } = getProductImage({
		screenshots,
		images,
		type,
		product,
		channels,
		channelInverse: false
	});

	// check program rating and block content accordingly
	const { isContentBlocked, placeholderRatingText } = useProductRatingCheck(pc_rating, since);

	const isLive = new Date(since).getTime() < new Date().getTime() && new Date(till).getTime() > new Date().getTime();

	const hash = isLive ? 'live' : 'program';

	const productLink = createLinkParams({
		id: uuid,
		type: subtype,
		title,
		hash
	});

	const handleLinkClick = () => {
		toggle();

		sendSearchDropdownEvent({
			url: productLink.pathname,
			genres,
			item_name: title,
			item_variant: pc_rating,
			release_year: year,
			country,
			runtime: duration
		});
	};

	return (
		<SearchItemStyled>
			<LinkStyled onClick={handleLinkClick} to={productLink}>
				<Preview>
					{isImgUrlValid && isChannelLogo && <ChannelImage src={imgUrl} alt="Logo" />}
					{isImgUrlValid && !isChannelLogo && <Image src={isContentBlocked ? NoImage : imgUrl} alt={title} />}
				</Preview>
				<Content>
					{isContentBlocked ? <ParagraphStyled>{placeholderRatingText}</ParagraphStyled> : title && <ParagraphStyled>{title}</ParagraphStyled>}

					{type === PRODUCT_TYPES[0] && <ProgramInfo product={product} channels={channels} />}

					{type === PRODUCT_TYPES[1] && <VodSerialInfo product={product} />}

					{type === PRODUCT_TYPES[2] && <VodSerialInfo product={product} />}
				</Content>
			</LinkStyled>
		</SearchItemStyled>
	);
};

const SearchItemStyled = styled.li`
	height: 9rem;
	border-top: 1px solid ${({ theme }) => theme.line};
`;

const Preview = styled.div`
	height: 7rem;
	width: 10rem;
	overflow: hidden;
`;

const Image = styled.img`
	height: 100%;
	width: 100%;
	object-fit: cover;
`;

const ChannelImage = styled.img`
	height: 100%;
	width: 100%;
	object-fit: contain;
`;

const Content = styled.div`
	flex: 1;
	overflow: hidden;
	padding-left: 2rem;
`;

const ParagraphStyled = styled(Paragraph.Ellipsis)`
	color: ${({ theme }) => theme.grayMedium};
	font-size: 1.5rem;
	${transitionMain}
`;

const LinkStyled = styled(Link)`
	&:link,
	&:visited {
		display: flex;
		height: 100%;
		width: 100%;
		padding: 1rem 1.5rem;
		${transitionMain}
	}

	&:hover,
	&:active {
		background: ${({ theme: { searchBox } }) => searchBox.itemHoverBg};

		${ParagraphStyled} {
			color: ${({ theme: { searchBox } }) => searchBox.itemTitleHover};
		}

		${Info} {
			color: ${({ theme: { searchBox } }) => searchBox.itemInfoHover};
		}

		${Clock} {
			g {
				fill: ${({ theme: { searchBox } }) => searchBox.itemInfoHover};
				${transitionMain}
			}
			path {
				fill: ${({ theme: { searchBox } }) => searchBox.itemInfoHover};
				${transitionMain}
			}
		}
	}
`;

SearchItem.propTypes = {
	product: PropTypes.shape({
		uuid: PropTypes.string.isRequired,
		images: PropTypes.object,
		screenshots: PropTypes.object,
		title: PropTypes.string.isRequired,
		type: PropTypes.string.isRequired
	}).isRequired,
	channels: PropTypes.array.isRequired,
	toggle: PropTypes.func.isRequired
};

export default SearchItem;
